<template>
  <table class="nutrition-facts">
    <tbody>
      <tr>
        <td>
          <permissions-control-wrapper
             v-if="showDownload"
             action="permissions.subscribed_plan.nutrition_label_printing"
            :required-general="$PERMISSIONS_GENERAL.DOWNLOAD"
            :required-subscribed-plan="$PERMISSIONS_SUBSCRIBED_PLAN.NUTRITION_LABEL_PRINTING"
            :permissions="permissions">
              <div slot="allowed" class="nf-labels-dropdown">
                <a class="button button--more-less is-small is-warning" @click="handleDownloadPDF">
                  <span class="more">
                    {{ $t('label.labels') }} <i class="far fa-angle-right" style="margin-left: 0.25em"></i>
                  </span>
                </a>
              </div>

              <div slot="not-allowed" class="nf-labels-dropdown">
                <a class="button button--more-less is-small is-warning is-disabled">
                  <span class="more">
                    {{ $t('label.labels') }} <i class="far fa-angle-down"></i>
                  </span>
                </a>
              </div>
            </div>
          </permissions-control-wrapper>
          <div class="nf-main-info no-border-bottom">
            <h4 class="nf-title">{{ $t('label.nutrition_facts') }}</h4>
          </div>
        </td>
      </tr>
      <tr>
        <td>
          <div v-if="mode === '100'" class="nf-main-info">
            <h5 class="nf-serving-size">{{ $t('label.serving_size') }} <span>100g</span></h5>
          </div>
          <div v-if="mode === 'quantity'" class="nf-main-info">
            <h5 class="nf-serving-size">{{ $t('label.quantity') }} <span>{{ productNutritionFacts.meta.food_weight_gr | number-format }}g</span></h5>
          </div>
          <div v-if="mode === 'selected-serving'" class="nf-main-info">
            <h5 class="nf-serving-size">{{ $t('label.total_weight') }} <span>{{ weight | number-format(0) }}g</span></h5>
          </div>
          <div v-if="mode === 'select-serving-size'" class="nf-main-info">
            <h5 class="nf-serving-size">{{ $t('label.serving_size') }}</h5>
            <div class="custom-select">
              <select v-model="predictServingSizeID" @change="changePredictServingSize">
                <template v-for="group in availableServing" v-if="group.list.length > 0">
                  <optgroup :label="group.title" :key="group.key">
                    <option v-for="serving in group.list" :value="serving.id" :selected="serving.id === predictServingSizeID">{{ serving.title || serving.description }} ({{ serving.weight_gr | number-format }}g)</option>
                  </optgroup>
                </template>
              </select>
            </div>
          </div>
        </td>
      </tr>
      <tr>
        <td>
          <div id="nf-energy-info-region" class="nf-energy-info">
            <div class="nf-border double-line-bottom">
              <!-- European label format -->
              <template v-if="_get(productNutritionFacts, 'meta.label_format') === NUTRITION_FACTS_LABEL_FORMAT.EUROPEAN">
                <h5 class="nf-title">
                  <span class="nf-label">{{ _get(energyList, 'name') }}</span>
                  <span class="nf-value">
                    <template v-for="(nutrientDefinition, index) in energyList.values">
                      <span :class="nutrientDefinition.values[0].unit">
                      {{ nutrientDefinition.values[0].quantity | number-format(0) }} {{ nutrientDefinition.values[0].unit }}
                      </span>
                      <span v-if="index === 0"> / </span>
                    </template>
                  </span>
                </h5>
              </template>

              <!-- USA label format -->
              <template v-else-if="_get(productNutritionFacts, 'meta.label_format') === NUTRITION_FACTS_LABEL_FORMAT.AMERICAN">
                <h5 class="nf-title is-us-label-format">
                  <span class="nf-label">{{ _get(energyList, 'us_title') }}</span>
                  <span class="nf-value">
                    <template v-for="(nutrientDefinition, index) in energyList.values">
                      <span v-if="nutrientDefinition.values[0].unit === 'kcal'" :class="nutrientDefinition.values[0].unit">
                        {{ nutrientDefinition.values[0].quantity | number-format(0) }}
                      </span>
                    </template>
                  </span>
                </h5>
              </template>
            </div>
          </div>
        </td>
      </tr>
      <tr>
        <td>
          <div id="nf-top-ten-region" class="nf-top-ten">
            <ul class="nf-top-list">
              <li class="nf-header">
                <span class="nf-label">{{ $t('label.amount') }}</span>
                <span class="nf-value">{{ $t('label.daily_value') }}</span>
              </li>
              <li class="nf-top-content">
                <ul>
                  <li v-for="topTenGroup in topTenList.items">
                    <span class="nf-label">
                      <span>{{ getItemLabel(topTenGroup) }}</span> {{ topTenGroup.values[0].quantity | number-format(2, 10) }}{{ topTenGroup.values[0].unit }}
                    </span>
                    <span
                       class="nf-value is-have-title"
                      :title="_get(topTenGroup, 'values.1.quantity') !== '' ? $t('label.daily_recommended_intake', { quantity: _get(topTenGroup, 'values.1.quantity'), unit: _get(topTenGroup, 'values.1.unit') }) : $t('label.daily_recommended_intake_not_defined')">
                      <template v-if="_get(topTenGroup, 'values.1.percentage')">
                        {{ _get(topTenGroup, 'values.1.percentage') | number-format(1, 1) | sufix-if-number('%') }}
                      </template>
                      <template v-else>~</template>
                    </span>
                    <ul>
                      <li v-for="topTenItem in topTenGroup.children">
                        <span class="nf-label">
                          <span>{{ getItemLabel(topTenItem) }}</span> {{ topTenItem.values[0].quantity | number-format(2, 10) }}{{ topTenItem.values[0].unit }}
                        </span>
                        <span
                           class="nf-value gr-value is-have-title"
                          :title="_get(topTenItem, 'values.1.quantity') !== '' ? $t('label.daily_recommended_intake', { quantity: _get(topTenItem, 'values.1.quantity'), unit: _get(topTenItem, 'values.1.unit') }) : $t('label.daily_recommended_intake_not_defined')">
                          <template v-if="_get(topTenItem, 'values.1.percentage')">
                            {{ _get(topTenItem, 'values.1.percentage') | number-format(1, 1) | sufix-if-number('%') }}
                          </template>
                          <template v-else>~</template>
                        </span>
                      </li>
                    </ul>
                  </li>
                </ul>
              </li>
            </ul>
        </div>
        </td>
      </tr>
    </tbody>
    <tfoot>
      <tr>
        <td class="double-line-top">
          <div class="nf-important" :class="{ 'all-items': showAllImportant }">
            <div id="nf-important-region">
              <div class="nf-important-list">
                <template v-for="children in importantList.children">
                  <template v-for="important in children.items">
                    <span class="important-item" :class="{ 'is-essential': children.list === 'essential' }">
                      <strong>{{ getItemLabel(important) }}</strong>
                      <template v-if="_get(important, 'values.0.quantity')">
                        {{ _get(important, 'values.0.quantity') | number-format(2, 10) }}{{ _get(important, 'values.0.unit') }} <span class="is-have-title" :title="_get(important, 'values.1.quantity') !== '' ? $t('label.daily_recommended_intake', { quantity: _get(important, 'values.1.quantity'), unit: _get(important, 'values.1.unit') }) : $t('label.daily_recommended_intake_not_defined')">{{ important.values[1].percentage | number-format(1, 1) | sufix-if-number('%') }}</span>
                      </template>
                      <template v-else>~</template>
                    </span>
                    <span v-if="isNotLast(children.items, important)" class="dot" :class="{ 'is-essential': children.list === 'essential' }"> &#8226; </span>
                  </template>
                  <span v-if="isNotLast(importantList.children, children)" class="dot"> &#8226; </span>
                </template>
              </div>
            </div>
            <div class="more-less">
              <a @click="toggleShowImportant" class="button button--more-less is-small is-warning">
                <span class="more">{{ $t('label.more') }}</span>
                <span class="less">{{ $t('label.less') }}</span>
              </a>
            </div>
          </div>
        </td>
      </tr>
      <tr>
        <td class="thin-line-bottom">
          <div id="nf-cal-per-gram-region" class="nf-cal-per-gram">
            <div class="nf-cal-per-gram">
              <nuttrema-nutritions-stucture-bars
                :weight="weight"
                :nutritions="gangOfFourList">
              </nuttrema-nutritions-stucture-bars>
            </div>
          </div>
        </td>
      </tr>
      <tr>
        <td>
          <p class="nf-note">
            <template v-if="isKnownLifeStage">
              * {{ $t('label.daily_value_text_personal') }}
            </template>
            <template v-else>
              * {{ $t('label.daily_value_text_default') }}
            </template>
          </p>
        </td>
      </tr>
    </tfoot>
  </table>
</template>

<script>
/* eslint-disable object-shorthand */
import { find, last, indexOf, get } from 'lodash';
import NuttremaNutritionsStuctureBars from 'components/ui/label/nuttrema-nutritions-stucture-bars';

import { NUTRITION_FACTS_LABEL_FORMAT } from 'constants/values';

const lists = {
  energy: 'energy',
  topTen: 'top_ten',
  important: 'important',
  caloriesPerGram: 'calories_per_gram',
};

export default {
  name: 'nutrition-facts',
  props: {
    permissions: {
      required: false,
      default: null,
    },
    productNutritionFacts: {
      type: Object,
      required: true,
      default: null,
    },
    // modes - 100 (per 100g), quantity (weight_gr), select-serving-size (change serving size)
    mode: {
      type: String,
      required: false,
      default: '100',
    },
    showDownload: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      showAllImportant: false,
      NUTRITION_FACTS_LABEL_FORMAT,
      predictServingSizeID: get(this.productNutritionFacts, 'selected_serving.id', null),
    };
  },
  computed: {
    isKnownLifeStage() {
      return get(this.productNutritionFacts, 'meta.known_life_stage', false);
    },
    weight() {
      return get(this.productNutritionFacts, 'selected_serving.weight_gr', 0);
    },
    selectedServing() {
      return get(this.productNutritionFacts, 'selected_serving', {});
    },
    availableServing() {
      return get(this.productNutritionFacts, 'available_serving', []);
    },
    energyList() {
      return find(this.productNutritionFacts.nutrition_facts, item => item.list === lists.energy);
    },
    topTenList() {
      return find(this.productNutritionFacts.nutrition_facts, item => item.list === lists.topTen);
    },
    importantList() {
      return find(this.productNutritionFacts.nutrition_facts, item => item.list === lists.important);
    },
    gangOfFourList() {
      const nutritionSummarized = get(this.productNutritionFacts, 'nutrition_summarized', null);
      if (nutritionSummarized) return nutritionSummarized;

      const gang = {};
      const four = ['PROCNT', 'CHOCDF', 'FAT', 'ALC'];
      const topTenList = find(this.productNutritionFacts.nutrition_facts, item => item.list === lists.topTen);
      find(topTenList.items, (item) => {
        if (indexOf(four, item.code) !== -1) {
          gang[item.code] = {
            name: item.title || item.name,
            nutrition_value: item.values[0].quantity,
          };
        }
      });
      return gang;
    },
  },
  methods: {
    toggleShowImportant() {
      this.showAllImportant = !this.showAllImportant;
    },
    isNotLast(list, item) {
      return last(list) !== item;
    },
    handleCommand(command) {
      let action = 'download';
      switch (command) {
        case 'print':
          action = 'print';
          break;
        default:
          action = 'download';
          break;
      }
      this.$emit('downloadPDF', this.predictServingSizeID, action);
    },
    handleDownloadPDF() {
      this.$emit('downloadPDF', this.predictServingSizeID);
    },
    changePredictServingSize() {
      this.$emit('servingSizeChange', this.predictServingSizeID);
    },
    getItemLabel(item) {
      const labelFormat = get(this.productNutritionFacts, 'meta.label_format');
      const itemTitle = labelFormat === NUTRITION_FACTS_LABEL_FORMAT.AMERICAN ? get(item, 'us_title', '') || get(item, 'title', '') : get(item, 'title', '');
      const itemLabel = itemTitle || get(item, 'name', '');
      return itemLabel;
    },
  },
  watch: {
    'productNutritionFacts.selected_serving.id': {
      handler: function (value) {
        this.predictServingSizeID = value;
      },
      deep: true,
    },
  },
  components: {
    NuttremaNutritionsStuctureBars,
  },
};
</script>

<style lang="scss" scoped>
  .nutrition-facts {
    &.add-padding {
      padding: 0.5em 0.75em;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/ui/nutrition-information/nutrition-facts.vue